import { ISession } from '../../models/session';
import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { BasicService } from '../../providers/basic';
import { TranslateService } from '@ngx-translate/core';
import { ITag } from '../../models/tag';
import { addIndex, reduce, assoc, keys, equals, clone, map, find, propEq, of } from 'ramda';
import { TagsService } from './tags.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { mergeMap, tap } from 'rxjs/operators';
import { LiveeFilesService } from './livee.files.service';

const resolveThemeTagName = (session: ISession): string => {
  switch (session.type) {
    case 'eevent_main':
      return 'eevent.theme';
    case 'simple_session':
      return 'session.theme';
  }
  return '';
};

const LIVEE_TAGS_ATTR = {
  'session.survey': { type: 'text', w: 't-alias' },
  'session.theme': { type: 'text', w: 'style' },
  'eevent.theme': { type: 'text', w: 'style' },

  'eevent.config': { type: 'json', w: 'json', subTag: resolveThemeTagName },
  'eevent.schedule': { type: 'json', w: 'json' },
  'eevent.speakers': { type: 'json', w: 'json' },
  'eevent.sponsors': { type: 'json', w: 'json' },
  'eevent.documents': { type: 'json', w: 'json' },
  'eevent.pages': { type: 'json', w: 'json' },
  'session.features': { type: 'json', w: 'json' },

  'eevent.title': { type: 'text', w: 'base', subTag: 'eevent.config', subTagVarName: 'sessionTitle' },
  'session.description': { type: 'text', w: 'base', subTag: 'eevent.config', subTagVarName: 'sessionTitle' },

  'session.logo': { type: 'image', w: 'base', subTag: 'eevent.config', subTagVarName: 'logoName' },
  'session.background': {
    type: 'image',
    w: 'base',
    subTag: 'eevent.config',
    subTagVarName: 'backgroundName'
  },
  'eevent.logo': { type: 'image', w: 'base', subTag: 'eevent.config', subTagVarName: 'logoName' },
  'eevent.background': {
    type: 'image',
    w: 'base',
    subTag: 'eevent.config',
    subTagVarName: 'backgroundName'
  }
};

const getTagType = (name: string) => {
  return LIVEE_TAGS_ATTR[name] ? LIVEE_TAGS_ATTR[name].type : 'text';
};

const getWrapperType = (name: string) => {
  return LIVEE_TAGS_ATTR[name] ? LIVEE_TAGS_ATTR[name].w : 'base';
};

const getDefaultTag = (name: string): ITag => {
  const type = getTagType(name);
  return {
    value: '',
    description: '',
    type,
    name
  };
};

const getLiveeTag = (name: string, tags: Array<ITag>): ILiveeTagWrapper => {
  const tag: ITag = find(propEq('name', name), tags) || getDefaultTag(name);
  const w = getWrapperType(name);
  switch (w) {
    case 't-alias':
      return new LiveeTagTemplateAliasWrapper(tag);
    case 'style':
      return new LiveeTagStyleWrapper(tag);
    case 'json':
      return new LiveeTagJsonWrapper(tag);
    default:
      return new LiveeTagBaseWrapper(tag);
  }
};

export interface ILiveeTagWrapper {
  tag: ITag;
  value: any;
  preloadFiles: any;
  parse(tag: ITag): void;
  update(value: any, name: string): void;
  modified(): boolean;
  reset(tag: ITag): void;
}

export class LiveeTagBaseWrapper implements ILiveeTagWrapper {
  private tagOriginal: ITag;
  constructor(public tag: ITag) {
    if (!tag.type) {
      tag.type = getTagType(tag.name);
    }
    this.parse(tag);
    this.reset(tag);
    this.preloadFiles = {};
  }
  preloadFiles: any;
  get value(): any {
    return this.tag.value;
  }
  set value(v: any) {
    this.tag.value = v;
  }
  update(value: any, name: string): void {}
  parse(tag: ITag) {}
  modified(): boolean {
    return !equals(this.tag, this.tagOriginal) || keys(this.preloadFiles).length > 0;
  }
  reset(tag: ITag) {
    this.tag = tag;
    this.tagOriginal = clone(this.tag);
    this.preloadFiles = {};
  }
}

export class LiveeTagJsonWrapper extends LiveeTagBaseWrapper {
  constructor(public tag: ITag) {
    super(tag);
  }
  parse(tag: ITag) {
    try {
      tag.value = JSON.parse(tag.value);
    } catch (e) {
      tag.value = {};
    }
  }
  get value() {
    return this.tag.value;
  }
  set value(v: any) {
    this.tag.value = v;
  }
  update(value: any, name: string): void {
    if (value && name && this.value) {
      this.value[name] = value;
    }
  }
}

export class LiveeTagTemplateAliasWrapper extends LiveeTagBaseWrapper {
  protected templateName;
  protected _values = {};
  constructor(public tag: ITag) {
    super(tag);
  }
  get value(): any {
    const tagValue = this.tag.value || '(!--poll id="" alias="" page="" multipleSubmitLimit=0--)';
    const regV = /(\w+)\s*=\s*"([^"]*)"|(\w+)\s*=\s*([\d]*)/gm;
    const regN = /\(!--(\w+)/;
    let m = regV.exec(tagValue);
    while (m) {
      const name = m[1] || m[3];
      const value = m[2] || m[4];
      this._values[name] = value || '';
      m = regV.exec(tagValue);
    }
    m = regN.exec(tagValue);
    if (m) {
      this.templateName = m[1];
    }
    return this._values['alias'];
  }
  set value(s: any) {
    this._values['alias'] = s;
    const val = keys(this._values)
      .map(n => {
        const v = /^\d+$/.test(this._values[n]) ? this._values[n] : `"${this._values[n]}"`;
        return `${new String(n)}=${v}`;
      })
      .join(' ');
    this.tag.value = `(!--${this.templateName} ${val}--)`;
  }
}

export class LiveeTagStyleWrapper extends LiveeTagBaseWrapper {
  protected template = `<style>
.text-color-1{color:MAIN_COLOR!important;}
.bg-color-1{background-color:MAIN_COLOR!important;}
.btn-color-1{background-color:MAIN_COLOR!important; border-color:MAIN_COLOR!important;}
.border-color-1{border-color:MAIN_COLOR!important;}
.bg-block{background-color:BLOCK_COLOR!important;}
.title-font{font-weight:TITLE_BOLD_OPTION!important; font-style:TITLE_ITALIC_OPTION!important;}
.text-font{font-weight:TEXT_BOLD_OPTION!important;}
.rounded-event{border-radius:RADIUS_OPTION!important;}
</style>`;
  protected valueMap = {
    mainColor: { placeholder: 'MAIN_COLOR', resolve: value => (value ? value : 'MAIN_COLOR') },
    blockColor: { placeholder: 'BLOCK_COLOR', resolve: value => (value ? value : 'BLOCK_COLOR') },
    titleBolb: { placeholder: 'TITLE_BOLD_OPTION', resolve: value => (value ? 'bold' : 'normal') },
    titleItalic: {
      placeholder: 'TITLE_ITALIC_OPTION',
      resolve: value => (value ? 'italic' : 'normal')
    },
    radius: {
      placeholder: 'RADIUS_OPTION',
      resolve: value => {
        switch (value) {
          case 'borderRadio1':
            return '0 0 0 0';
          case 'borderRadio2':
            return '0 15px 15px 0';
          case 'borderRadio3':
            return '15px 15px 15px 15px';
        }
        return 'RADIUS_OPTION';
      }
    }
  };
  constructor(public tag: ITag) {
    super(tag);
  }
  get value(): any {
    return this.tag.value;
  }
  set value(v: any) {
    this.tag.value = v;
  }
  update(value: any, name: string): void {
    if (value) {
      this.value = reduce(
        (acc, name) => {
          const vm = this.valueMap[name];
          if (vm) {
            acc = acc.replace(new RegExp(vm.placeholder, 'g'), vm.resolve(value[name]));
          }
          return acc;
        },
        this.template,
        keys<string>(value)
      );
    }
  }
}

@Injectable()
export class LiveeTagsService extends BasicService {
  constructor(
    protected auth: AuthService,
    protected translate: TranslateService,
    private tagsService: TagsService,
    private liveeFilesService: LiveeFilesService
  ) {
    super(auth, translate);
  }

  loadTag(session: ISession, name: string): any {
    return this.tagsService.getTag(session, name).pipe(
      mergeMap(tags => {
        return [getLiveeTag(name, tags)];
      })
    );
  }

  loadTags(session: ISession, names: Array<string>): any {
    const tagsToMap = tagss =>
      addIndex(reduce)(
        (acc, tags: any, i) => {
          const name = names[i];
          const tag = find(propEq('name', name), tags) || getDefaultTag(name);
          const liveeTag = getLiveeTag(names[i], tags);
          return assoc(name, liveeTag, acc);
        },
        {},
        tagss
      );
    return forkJoin(map(name => this.tagsService.getTag(session, name), names)).pipe(
      mergeMap(tagss => {
        return [tagsToMap(tagss)];
      })
    );
  }

  preloadFiles(session: ISession, ltag: ILiveeTagWrapper) {
    if (ltag.tag.type === 'json' && keys(ltag.preloadFiles).length > 0) {
      const pfiles = keys(ltag.preloadFiles).map((varName: any) => {
        const { name, data } = ltag.preloadFiles[varName];
        return this.liveeFilesService.uploadFileForAlias(session, name, varName, data)
      });
      return forkJoin(pfiles, f => {
        const files = Array.isArray(f) ? f : [f];
        files.forEach(file => {
          ltag.value[file.alias] = file.url;
        })
        return ltag;
      });
    }
    return of(ltag);
  }

  updateTagsOrImages(session: ISession, liveeTagsIndex) {
    const tagNames = keys<string>(liveeTagsIndex);
    return forkJoin(
      reduce(
        (acc: any, ltag) => {
          if (ltag.modified()) {
            console.log('>>> LTAG modified: ', JSON.stringify(ltag.preloadFiles));
            const tag = ltag.tag;
            return [
              ...acc,
              tag.type === 'image'
                ? this.tagsService.uploadImage(session, tag)
                : this.tagsService.updateTag(session, tag)
            ];
          }
          return acc;
        },
        [],
        tagNames.map<ILiveeTagWrapper>(name => liveeTagsIndex[name])
      )
    ).pipe(
      tap((tagss: Array<any>) => {
        this.postUpdate(session, clone(tagss));
        tagss.forEach((tags: any) => {
          // after update we can be sure in retrieved tag structure
          const tagName = tags[0].name;
          const ltag = getLiveeTag(tagName, tags);
          if (liveeTagsIndex[tagName]) {
            liveeTagsIndex[tagName].reset(ltag.tag);
          }
        });
      })
    );
  }

  updateTags(session: ISession, liveeTagsIndex) {
    return forkJoin(reduce((acc: any, ltag: ILiveeTagWrapper) => {
      return [ ...acc, this.preloadFiles(session, ltag)]; 
    }, [], keys(liveeTagsIndex).map<ILiveeTagWrapper>(name => liveeTagsIndex[name])))
      .pipe(mergeMap(() => {
        return this.updateTagsOrImages(session, liveeTagsIndex);
      }));
  }

  private postUpdate(session: ISession, tagss: Array<any>) {
    // collect sub tags
    const upd = addIndex(reduce)(
      (acc: any, tags: any, i) => {
        // after update we can be sure in retrieved tag structure
        const tagName = tags[0].name;
        const ltag = getLiveeTag(tagName, tags);
        if (LIVEE_TAGS_ATTR[tagName] && LIVEE_TAGS_ATTR[tagName].subTag) {
          const subTagName =
            typeof LIVEE_TAGS_ATTR[tagName].subTag === 'function'
              ? LIVEE_TAGS_ATTR[tagName].subTag(session)
              : LIVEE_TAGS_ATTR[tagName].subTag;
          if (subTagName) {
            const updates = acc[subTagName] || [];
            acc = assoc(
              subTagName,
              [
                ...updates,
                { value: clone(ltag.value), tagVarName: LIVEE_TAGS_ATTR[tagName].subTagVarName }
              ],
              acc
            );
          }
        }
        return acc;
      },
      {},
      tagss
    );

    this.loadTags(session, keys(upd))
      .pipe(
        mergeMap((tagsIndex: any) => {
          return forkJoin(
            keys(tagsIndex)
              .map(name => {
                const ltag: ILiveeTagWrapper = tagsIndex[name];
                const updates = upd[ltag.tag.name] || [];
                updates.forEach(u => {
                  ltag.update(u.value, u.tagVarName);
                });
                return ltag.tag;
              })
              .map(tag => this.tagsService.updateTag(session, tag))
          );
        })
      )
      .subscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/livee.tags.service.ts